import React, { useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import SEO from '../components/SEO';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.submitContact(form.name, form.email, form.message);
      toast.success('Message received. We will respond as soon as possible.');
      setForm({ name: '', email: '', message: '' });
    } catch {
      toast.error('Unable to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#050505] text-white" data-testid="contact-page">
      <SEO
        title="Contact Bronto Platforms — Get in Touch"
        description="Contact Bronto Platforms Private Limited. Reach our team for general inquiries, partnerships, media requests, and career opportunities."
        path="/contact"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Contact
          </div>
          <h1 className="text-display text-5xl md:text-7xl lg:text-8xl mb-10 max-w-5xl">
            Get in <span className="rainbow-text">touch.</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/65 max-w-3xl leading-relaxed font-light">
            For inquiries related to Bronto Platforms, our platforms, partnerships, media, or career opportunities, please reach out using the form below.
          </p>
          <p className="mt-4 text-sm text-white/45">
            Messages are routed to <span className="text-white/70 font-medium">info@brontointelligence.com</span>.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="Your full name"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="you@example.com"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3">
                Message
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={7}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="How can we help?"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <Mail className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-px bg-white/5 rounded-3xl overflow-hidden">
          {[
            { label: 'General', desc: 'For general inquiries, partnerships, and information.' },
            { label: 'Media', desc: 'For journalists, editors, and press representatives.' },
            { label: 'Careers', desc: 'For career opportunities and recruitment.' },
          ].map((item) => (
            <div key={item.label} className="bg-[#050505] p-8 md:p-10">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
                {item.label}
              </div>
              <p className="text-base text-white/70 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Contact;
